import { Canvas, useFrame, type Camera } from '@react-three/fiber';
import * as THREE from 'three';

import {
  DEFAULT_VIEWER_POSITION,
  selectedLayersAtom,
  setProjectInitialPosition,
  updateViewerStateWithInitialPosition,
  viewerStateAtomFamily,
} from '@/atoms';
import { groupImagesByPosition2, project } from '@/core';
import { execute } from '@/graphql/execute';
import { GoogleImagesByPositionQuery } from '@/graphql/graphql';
import { cn } from '@/lib/utils';
import Layers from '@/pages/Project/Viewer/Layers';
import { featuresByLayerQuery } from '@/queries/feature.query';
import { googleImagesByPositionQuery } from '@/queries/googleImage.query';
import { imagesByLayerQuery } from '@/queries/image.query';
import { getProjectQuery } from '@/queries/project.query';
import {
  AccessibilitySolid,
  CameraSolid,
  CircleSolid,
  EyeSlashSolid,
  InfoCircleSolid,
  Map,
  RulerSolid,
  SmileGhostSolid,
} from '@mynaui/icons-react';
import { CameraControls, CameraControlsImpl, OrthographicCamera, Stats } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import 'mapbox-gl/dist/mapbox-gl.css';
import posthog from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  FunctionComponent,
  PropsWithChildren,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type SetStateAction,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import FeatureProperties from './FeatureProperties';
import { GeoJson } from './GeoJson';
import GoogleStreetView from './GoogleStreetView';
import { IFC } from './IFC';
import { IPRJ } from './IPRJ2';
import { LandXML } from './LandXML';
import LayerProperties from './LayerProperties';
import { Loader } from './Loader';
import { PointCloudContextMenu } from './PointCloudContextMenu';
import { PointCloud } from './Pointcloud';
import PortalTooltip from './PortalTooltip';
import { Joystick } from './Viewer/Joystick';
import { Xgrids } from './Xgrids';
const ViewerMap = lazy(() => import('./Map').then((module) => ({ default: module.default })));

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

interface iViewerProps extends PropsWithChildren {
  projectId: string;
  hideMap?: boolean;
  showImages?: boolean;
}

const Viewer: FunctionComponent<iViewerProps> = ({ projectId, children, hideMap = false, showImages = true }) => {
  const [hidePointcloud, setHidePointcloud] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [showGoogleStreetView, setShowGoogleStreetView] = useState(false);
  const [orthoCamera, setOrthoCamera] = useState(false);
  const [thirdPersonFlying, setThirdPersonFlying] = useState(false);
  const [showJoystick, setShowJoystick] = useState(true);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const isGoogleStreetViewEnabled = useFeatureFlagEnabled('googleStreetview');

  useEffect(() => {
    const checkTouchDevice = () => {
      // More comprehensive touch detection for iOS Safari and other devices
      const hasTouch =
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - for older browsers
        (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0) ||
        // Check for iOS specifically
        (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream);

      setIsTouchDevice(hasTouch);
    };
    checkTouchDevice();
  }, []);

  const [params, setParams] = useSearchParams();
  const isMeasurementsEnabled = params.get('showMeasurements') === 'true';

  const [positionWGS84, setPositionWGS84] = useState<THREE.Vector3 | null>(null);

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });

  const { data: googleImagesData } = useQuery({
    queryKey: ['googleImagesByPosition', positionWGS84],
    queryFn: () => execute(googleImagesByPositionQuery, { lat: positionWGS84!.y, lng: positionWGS84!.x }),
    enabled: positionWGS84 !== null && showGoogleStreetView,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (googleImagesData?.images.length === 0) {
      toast.error('No Google Street View images found nearby.');
      setShowGoogleStreetView(false);
    }
  }, [googleImagesData?.images]);

  const [googleImages, setGoogleImages] = useState<
    (GoogleImagesByPositionQuery['images'][number] & { location: { coords: [number, number, number]; srid: string } })[]
  >([]);

  const layers = useAtomValue(selectedLayersAtom(projectId));
  const pointcloudLayers = useMemo(() => layers.filter((l) => l.type === 'POINTCLOUD'), [layers]);
  const tileLayers = useMemo(() => layers.filter((l) => l.type === 'TILE'), [layers]);
  const ifcModelLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'IFC'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layers
        .filter((l) => l.type === 'MODEL' && l.metadata?.type === 'IFC')
        .map((l) => l.id)
        .join(','),
    ],
  );
  const landxmlLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'LANDXML'),
    [layers],
  );
  const xgridsLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'XGRIDS'),
    [layers],
  );

  const { data: imagesData } = useQuery({
    queryKey: [
      'images',
      layers
        .filter((l) => l.type === 'IMAGE')
        .map((l) => l.id)
        .join(','),
    ],
    queryFn: () => execute(imagesByLayerQuery, { layerIds: layers.filter((l) => l.type === 'IMAGE').map((l) => l.id) }),
  });

  const vectorLayers = useMemo(() => layers.filter((layer) => layer.type === 'VECTOR'), [layers]);

  const featureQueries = useQueries({
    queries: vectorLayers.map((layer) => ({
      queryKey: ['features', layer.id],
      queryFn: () => execute(featuresByLayerQuery, { layerIds: [layer.id] }),
    })),
  });

  const features = useMemo(
    () => featureQueries.flatMap((query) => query.data?.featuresByLayer ?? []),
    [
      // featureQueries is always a new aray every single render. its retarded
      JSON.stringify(featureQueries.map((q) => ({ isSuccess: q.isSuccess, data: q.data?.featuresByLayer }))),
    ],
  );

  const _handleToggleMeasurements = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('showMeasurements', isMeasurementsEnabled ? 'false' : 'true');
    posthog.capture(isMeasurementsEnabled ? 'Project Measurements Disabled' : 'Project Measurements Enabled');
    setParams(params);
  };

  const images = useMemo(() => imagesData?.imagesByLayer ?? [], [imagesData?.imagesByLayer]);

  const [viewerState, setViewerState] = useAtom(viewerStateAtomFamily(projectId));

  // Update viewer state with initial position when project data is available
  useEffect(() => {
    if (projectData?.project?.initialPosition) {
      setProjectInitialPosition(projectId, projectData.project.initialPosition);
      updateViewerStateWithInitialPosition(projectId, setViewerState);
    }
  }, [projectData?.project?.initialPosition, projectId, setViewerState]);
  const position = useMemo(
    () => new THREE.Vector3(viewerState.position.x, viewerState.position.y, viewerState.position.z),
    [viewerState.position.x, viewerState.position.y, viewerState.position.z],
  );
  const setPosition = useCallback(
    (value: SetStateAction<THREE.Vector3>) => {
      setViewerState((prev) => {
        const current = new THREE.Vector3(prev.position.x, prev.position.y, prev.position.z);
        const next = typeof value === 'function' ? value(current) : value;

        return {
          ...prev,
          position: {
            x: next.x ?? DEFAULT_VIEWER_POSITION.x,
            y: next.y ?? DEFAULT_VIEWER_POSITION.y,
            z: next.z ?? DEFAULT_VIEWER_POSITION.z,
          },
        };
      });
    },
    [setViewerState],
  );
  const controlRef = useRef<CameraControls | null>(null);
  const cameraRef = useRef<Camera>(null);
  const { imageId, googleImageId } = viewerState;
  const setImageId = useCallback(
    (value: SetStateAction<string | null>) => {
      setViewerState((prev) => ({
        ...prev,
        imageId: typeof value === 'function' ? value(prev.imageId) : value,
      }));
    },
    [setViewerState],
  );
  const setGoogleImageId = useCallback(
    (value: SetStateAction<string | null>) => {
      setViewerState((prev) => ({
        ...prev,
        googleImageId: typeof value === 'function' ? value(prev.googleImageId) : value,
      }));
    },
    [setViewerState],
  );
  const [hoverImageId, setHoverImageId] = useState<string | null>(null);

  useEffect(() => {
    if (!projectData?.project.srid) return;
    (async () => {
      project([position.x, position.y, position.z], `EPSG:${projectData?.project.srid}`).then((coords) =>
        setPositionWGS84(new THREE.Vector3(...coords)),
      );
    })();
  }, [position.x, position.y, position.z, projectData?.project.srid]);

  useEffect(() => {
    if (!projectData) return;
    if (!googleImagesData?.images) return setGoogleImages([]);

    Promise.all(
      googleImagesData.images.map(async (image) => {
        return {
          ...image,
          location: {
            coords: await project(
              [image.locationWGS84.coords[0], image.locationWGS84.coords[1], image.locationWGS84.coords[2]],
              'EPSG:4326',
              `EPSG:${projectData.project.srid}`,
            ),
            srid: `EPSG:${projectData.project.srid}`,
          },
        };
      }),
    ).then((images) => setGoogleImages(images));
  }, [googleImagesData?.images]);

  const imagePositions = useMemo(() => {
    if (!images) return null;

    function getCamerasCentroid(imageList: typeof images) {
      const centroid = new THREE.Vector3();
      for (const img of imageList) {
        centroid.add(new THREE.Vector3(img.location.coords[0], img.location.coords[1], img.location.coords[2]));
      }

      centroid.divideScalar(imageList.length);

      return centroid;
    }

    const allPositions = groupImagesByPosition2(images);
    return allPositions.map((imagePosition) => ({
      ...imagePosition,
      display: new THREE.Vector3(...imagePosition.xyz).distanceTo(position) <= 35,
      cameraPosition: getCamerasCentroid(imagePosition.entries),
      renderPosition: getCamerasCentroid(imagePosition.entries).sub(new THREE.Vector3(0, 0, 1.5)),
    }));
  }, [images, position]);

  useEffect(() => {
    if (!imagePositions || imagePositions.length === 0) return setImageId(null);
    const imagePosition = imagePositions.find((p) => p.entries[0].id === imageId);

    if (imagePosition) return;

    // Find the closest image to the current position
    let closestImagePosition = imagePositions[0];
    let closestDistance = Infinity;

    imagePositions.forEach((imagePosition) => {
      const distance = cameraRef.current?.position.distanceTo(new THREE.Vector3(...imagePosition.xyz)) ?? Infinity;
      if (distance < closestDistance) {
        closestDistance = distance;
        closestImagePosition = imagePosition;
      }
    });

    setImageId(closestImagePosition.entries[0].id);
  }, [position, imagePositions, imageId, setImageId]);

  const isFlying = (layers.every((l) => l.type !== 'IMAGE') || !showImages) && !showGoogleStreetView;
  // const isFlying = false;

  const googleImage = useMemo(
    () => googleImages.find((img) => img.id === googleImageId) || null,
    // hacky solution to circumvent url update in googleImage causing texture loader to load it twice
    [googleImageId, googleImages.some((img) => img.id === googleImageId)],
  );

  if (!projectData) return null;

  return (
    <div className='flex flex-1 overflow-hidden w-full relative z-10'>
      <div
        id='canvas-container'
        tabIndex={0}
        className='overflow-hidden flex-1 min-w-0 relative bg-slate-100/70 dark:bg-stone-900/70'>
        <Suspense fallback={null}>
          {showMap && !hideMap && projectData.project.srid !== -1 && (
            <ViewerMap
              images={images}
              googleImages={googleImages}
              showGoogleStreetView={showGoogleStreetView}
              features={features}
              projection={`EPSG:${projectData.project.srid}`}
              setImageId={setImageId}
              setGoogleImageId={setGoogleImageId}
              imageId={imageId}
              googleImageId={googleImageId}
              cameraRef={cameraRef}
              hoverImageId={hoverImageId}
              setHoverImageId={setHoverImageId}
              layers={tileLayers}
            />
          )}
        </Suspense>
        <Canvas
          // @ts-ignore
          raycaster={{ params: { Line: { threshold: 0.1 }, Points: { threshold: 0.01 } } }}
          camera={{ fov: 60, far: 10_000 }}>
          {window.location.hostname === 'localhost' || window.location.hostname === 'local.skygis.app' ? (
            <Stats />
          ) : null}
          <OrthographicCamera
            near={0.000001}
            far={10_000}
            // zoom={10}
            zoom={10}
            makeDefault={orthoCamera}
          />
          <Controls
            cameraRef={cameraRef}
            position={position}
            flying={isFlying}
            thirdperson={thirdPersonFlying || orthoCamera}
            isTouchDevice={isTouchDevice}
            initialAngle={projectData.project.initialAngle}
          />
          <group position={position}>
            <directionalLight position={new THREE.Vector3(10, 10, 0)} intensity={0.3} color='white' castShadow />
          </group>
          <hemisphereLight color={0xeeeeff} groundColor={0x777788} intensity={0.2} castShadow />
          <ambientLight intensity={0.3} castShadow />
          {showGoogleStreetView ? (
            <Suspense fallback={<Loader />}>
              <GoogleStreetView
                imageId={googleImageId}
                googleImage={googleImage}
                setImageId={setGoogleImageId}
                setPosition={setPosition}
                position={position}
                projection={`EPSG:${projectData.project.srid}`}
                hoverImageId={hoverImageId}
                setHoverImageId={setHoverImageId}
                googleImages={googleImages}
                positionWGS84={positionWGS84}
              />
            </Suspense>
          ) : null}
          {showGoogleStreetView ? null : (
            <>
              <PointCloud
                visible={!hidePointcloud}
                layers={pointcloudLayers}
                controlRef={controlRef}
                project={projectData.project}
              />
              <GeoJson
                features={features}
                flying={isFlying}
                layers={vectorLayers}
                // onFeatureCollectionChange={setFeatureCollection}
              />
              {/* Shared lighting for IFC and LandXML models */}
              <directionalLight position={[1, 0.5, 2]} intensity={1.2} color='white' />
              <directionalLight position={[-0.5, -0.5, 0.5]} intensity={0.3} color={0x8899aa} />
              <ambientLight intensity={0.15} />
              <IFC layers={ifcModelLayers} />
              <LandXML layers={landxmlLayers} />
              <Xgrids layers={xgridsLayers} />
              {showImages && (
                <IPRJ
                  setPosition={setPosition}
                  // there's a bug, imagesLayer is sometimes undefined
                  position={position}
                  images={images}
                  imagePositions={imagePositions}
                  layers={layers.filter((l) => l.type === 'IMAGE')}
                  imageId={imageId}
                  setImageId={setImageId}
                  hoverImageId={hoverImageId}
                  setHoverImageId={setHoverImageId}
                />
              )}
            </>
          )}
        </Canvas>
        {children}
        <div className='absolute z-10 bottom-2 right-2 flex gap-2'>
          {showGoogleStreetView ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${positionWGS84?.y},${positionWGS84?.x}`)}`}
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 opacity-70 hover:opacity-100 transition-all duration-200'>
              © {googleImage ? googleImage.copyright : 'Google Street View'}
            </a>
          ) : null}
        </div>
      </div>
      <FeatureProperties />
      <LayerProperties />
      <ErrorBoundary
        fallback={<div className='flex flex-col items-center justify-center px-4'>Failed to load layers</div>}>
        <Layers projectId={projectId} />
      </ErrorBoundary>
      {isTouchDevice && isFlying && (
        <Joystick
          visible={showJoystick}
          onMove={() => {
            // Movement is handled in the Controls component via custom events
          }}
          onEnd={() => {
            // Reset is handled in the Controls component via custom events
          }}
        />
      )}
      <div className='absolute z-10 bottom-2 left-2 flex gap-2'>
        {!hideMap && (
          <ToggleIconButton
            active={showMap}
            icon={<Map />}
            tooltip={showMap ? 'Hide Map' : 'Show Map'}
            disabled={projectData.project.srid === -1}
            onClick={() => setShowMap(!showMap)}
          />
        )}
        {isGoogleStreetViewEnabled && (
          <ToggleIconButton
            active={showGoogleStreetView}
            icon={<AccessibilitySolid />}
            tooltip={showGoogleStreetView ? 'Hide Google Street View' : 'Show Google Street View'}
            onClick={() => setShowGoogleStreetView(!showGoogleStreetView)}
          />
        )}
        {pointcloudLayers?.length > 0 ? (
          <>
            <ToggleIconButton
              active={isMeasurementsEnabled}
              icon={<RulerSolid />}
              tooltip={isMeasurementsEnabled ? 'Stop Measuring' : 'Measure distance'}
              onClick={() => _handleToggleMeasurements()}
            />
            <ToggleIconButton
              active={hidePointcloud}
              icon={<EyeSlashSolid />}
              tooltip={hidePointcloud ? 'Show Pointcloud' : 'Hide Pointcloud'}
              onClick={() => setHidePointcloud(!hidePointcloud)}
            />
          </>
        ) : null}
        {isFlying ? (
          <>
            {isTouchDevice && (
              <ToggleIconButton
                active={showJoystick}
                icon={<CircleSolid />}
                tooltip={showJoystick ? 'Hide Joystick' : 'Show Joystick'}
                onClick={() => setShowJoystick(!showJoystick)}
              />
            )}
            <ToggleIconButton
              icon={<InfoCircleSolid />}
              tooltip={
                <>
                  <div className='text-sm pt-1'>
                    <div className='font-semibold mb-2'>Flying Controls</div>
                    <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-xs pb-2'>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>W</kbd>{' '}
                        Forward
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>S</kbd>{' '}
                        Backward
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>A</kbd>{' '}
                        Left
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>D</kbd>{' '}
                        Right
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>
                          Space
                        </kbd>{' '}
                        Up
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>E</kbd> Up
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>Q</kbd>{' '}
                        Down
                      </div>
                      <div>
                        <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1 rounded'>
                          Shift
                        </kbd>{' '}
                        Speed up
                      </div>
                    </div>
                  </div>
                </>
              }
            />
            <ToggleIconButton
              active={thirdPersonFlying}
              disabled={orthoCamera}
              icon={<SmileGhostSolid />}
              tooltip='3rd Person Flying'
              onClick={() => setThirdPersonFlying(!thirdPersonFlying)}
            />
            <ToggleIconButton
              active={orthoCamera}
              icon={<CameraSolid />}
              tooltip='Orthographic Camera'
              onClick={() => setOrthoCamera(!orthoCamera)}
            />
          </>
        ) : null}
      </div>
      <PointCloudContextMenu />
    </div>
  );
};

const ToggleIconButton = ({
  icon,
  tooltip,
  onClick,
  disabled,
  active,
}: {
  icon: React.ReactNode;
  tooltip: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
}) => {
  const id = useId();
  return (
    <>
      <button
        data-tooltip-id={id}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'size-8 rounded-md flex items-center justify-center [&>svg]:size-4 [&>svg]:stroke-2 border disabled:opacity-70 backdrop-blur-md',
          active && !disabled
            ? 'bg-slate-800/80 text-white border-slate-800/70 hover:bg-slate-800 active:bg-slate-900 dark:bg-white dark:border-slate-200/70 dark:text-stone-700'
            : 'bg-white/70 border-slate-200/70 backdrop-blur-md text-slate-700 hover:bg-slate-50 active:bg-slate-100 dark:bg-stone-900/80 dark:border-stone-700/70 dark:text-stone-100',
        )}>
        {icon}
      </button>
      <PortalTooltip id={id}>{tooltip}</PortalTooltip>
    </>
  );
};

const calculateControlSpeed = (camera: Camera, canvas: HTMLCanvasElement, flying: boolean, thirdperson: boolean) => {
  if (camera instanceof THREE.OrthographicCamera) return 0.6;
  const H = canvas.clientHeight;
  const fov = camera.getEffectiveFOV();
  const vfov = THREE.MathUtils.degToRad(fov);
  const exact = (H / Math.PI) * Math.atan(Math.tan(vfov / 2) * (1 / H));
  if (flying && thirdperson) return exact * 2;
  return -exact;
};

type KeyMap = {
  KeyW: boolean;
  KeyA: boolean;
  KeyS: boolean;
  KeyD: boolean;
  KeyQ: boolean;
  KeyE: boolean;
  Space: boolean;
  ShiftLeft: boolean;
};

const useKeyboard = (): KeyMap => {
  const [keys, setKeys] = useState<KeyMap>({
    KeyW: false,
    KeyA: false,
    KeyS: false,
    KeyD: false,
    KeyQ: false,
    KeyE: false,
    Space: false,
    ShiftLeft: false,
  });

  useEffect(() => {
    const el = document.getElementById('canvas-container');
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code in keys) setKeys((k) => ({ ...k, [e.code as keyof KeyMap]: true }));
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code in keys) setKeys((k) => ({ ...k, [e.code as keyof KeyMap]: false }));
    };
    el.addEventListener('keydown', onKeyDown, true);
    el.addEventListener('keyup', onKeyUp, true);
    return () => {
      el.removeEventListener('keydown', onKeyDown, true);
      el.removeEventListener('keyup', onKeyUp, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return keys;
};

const Controls = ({
  cameraRef,
  position,
  flying,
  thirdperson,
  isTouchDevice,
  initialAngle,
}: {
  cameraRef: RefObject<Camera | null>;
  position: THREE.Vector3;
  flying: boolean;
  thirdperson: boolean;
  isTouchDevice: boolean;
  initialAngle: number;
}) => {
  const camera = useThree((state) => state.camera);
  const canvas = useThree((state) => state.gl.domElement);
  const cameraControlsRef = useRef<CameraControlsImpl>(null);
  const keys = useKeyboard();
  const hasOrientedToInitialAngle = useRef(false);
  const joystickInputRef = useRef({ x: 0, y: 0 });

  const up = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const down = useMemo(() => new THREE.Vector3(0, 0, -1), []);
  const forward = useMemo(() => new THREE.Vector3(), []);
  const right = useMemo(() => new THREE.Vector3(), []);
  const move = useMemo(() => new THREE.Vector3(), []);

  const [rotateSpeed, setRotateSpeed] = useState(calculateControlSpeed(camera, canvas, flying, thirdperson));

  const isOrthoCamera = camera instanceof THREE.OrthographicCamera;

  // Expose joystick input handler
  useEffect(() => {
    if (!isTouchDevice) return;

    const handleJoystickMove = (event: CustomEvent<{ x: number; y: number }>) => {
      joystickInputRef.current = event.detail;
    };

    const handleJoystickEnd = () => {
      joystickInputRef.current = { x: 0, y: 0 };
    };

    window.addEventListener('joystick-move' as any, handleJoystickMove);
    window.addEventListener('joystick-end' as any, handleJoystickEnd);

    return () => {
      window.removeEventListener('joystick-move' as any, handleJoystickMove);
      window.removeEventListener('joystick-end' as any, handleJoystickEnd);
    };
  }, [isTouchDevice]);

  useEffect(() => {
    if (!cameraControlsRef.current) return;
    if (flying && cameraRef.current === camera) return;

    if (cameraRef.current && cameraRef.current !== camera && camera instanceof THREE.OrthographicCamera) {
      camera.position.copy(cameraRef.current.position);
    }

    cameraRef.current = camera;
    let theta, phi;
    if (!hasOrientedToInitialAngle.current) {
      theta = -initialAngle * THREE.MathUtils.DEG2RAD;
      phi = Math.PI / 2;
      cameraControlsRef.current.rotateTo(0, Math.PI / 2, false);
      hasOrientedToInitialAngle.current = true;
      // return;
    }

    const eulerWorld = cameraRef.current.rotation.clone();
    cameraControlsRef.current
      .normalizeRotations()
      .setLookAt(
        position.x - 0.00001,
        position.y - 0.00001,
        position.z - 0.00001,
        position.x,
        position.y,
        position.z,
        false,
      );

    const offset = new THREE.Vector3(0, 0, -1)
      .applyEuler(eulerWorld)
      .clone()
      .multiplyScalar(-1)
      .clone()
      .applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
    const s = new THREE.Spherical().setFromVector3(offset);

    cameraControlsRef.current.rotateTo(theta ?? s.theta, phi ?? s.phi, false);
  }, [cameraControlsRef, position, flying, camera]);

  useFrame((_, delta) => {
    if (!flying) return;

    camera.getWorldDirection(forward);
    // forward.z = 0;
    forward.normalize();

    right.crossVectors(up, forward).normalize();

    // Combine keyboard and joystick input
    const f = (keys.KeyW ? 1 : 0) - (keys.KeyS ? 1 : 0) + (isTouchDevice ? joystickInputRef.current.y : 0);
    const r = (keys.KeyA ? 1 : 0) - (keys.KeyD ? 1 : 0) + (isTouchDevice ? joystickInputRef.current.x : 0);
    const u = keys.Space || keys.KeyE ? 1 : 0;
    const d = keys.KeyQ ? 1 : 0;

    move.set(0, 0, 0);
    if (f) move.addScaledVector(forward, f);
    if (r) move.addScaledVector(right, r);
    if (u) move.addScaledVector(up, u);
    if (d) move.addScaledVector(down, d);

    if (move.lengthSq() > 0 && cameraRef.current && cameraControlsRef.current) {
      move.normalize();
      const speed = 10;
      const v = (keys.ShiftLeft ? 4 : 1) * speed * delta;
      const newPosition = cameraRef.current.position.clone().addScaledVector(move, v);
      if (!thirdperson) {
        cameraControlsRef.current.moveTo(newPosition.x, newPosition.y, newPosition.z, false);
      } else {
        const newTarget = cameraControlsRef.current.getTarget(new THREE.Vector3()).addScaledVector(move, v);
        cameraControlsRef.current.setLookAt(
          newPosition.x,
          newPosition.y,
          newPosition.z,
          newTarget.x,
          newTarget.y,
          newTarget.z,
          false,
        );
      }
    }
  });

  useEffect(() => {
    if (!cameraControlsRef.current) return;

    if (flying && thirdperson) {
      cameraControlsRef.current.maxDistance = Infinity;
      cameraControlsRef.current.dollyTo(50);
      cameraControlsRef.current.update(1);
    } else if (flying && !thirdperson) {
      cameraControlsRef.current.maxDistance = 0.00001;
      cameraControlsRef.current.dollyTo(0.00001);
      cameraControlsRef.current.update(1);
    }
  }, [flying, thirdperson, isOrthoCamera]);

  return (
    <CameraControls
      minDistance={Number.EPSILON}
      maxDistance={flying && thirdperson ? Infinity : isOrthoCamera ? 100 : 0.00001}
      minPolarAngle={0.017}
      maxPolarAngle={Math.PI - 0.017}
      smoothTime={0}
      draggingSmoothTime={0}
      azimuthRotateSpeed={rotateSpeed}
      polarRotateSpeed={rotateSpeed}
      truckSpeed={6}
      enabled
      ref={cameraControlsRef}
      camera={camera}
      restThreshold={0.05}
      onRest={() => setRotateSpeed(calculateControlSpeed(camera, canvas, flying, thirdperson))}
      mouseButtons={{
        left: CameraControlsImpl.ACTION.ROTATE,
        right: flying && thirdperson ? CameraControlsImpl.ACTION.TRUCK : CameraControlsImpl.ACTION.NONE,
        middle: flying && thirdperson ? CameraControlsImpl.ACTION.DOLLY : CameraControlsImpl.ACTION.NONE,
        wheel:
          flying && thirdperson && !isOrthoCamera ? CameraControlsImpl.ACTION.DOLLY : CameraControlsImpl.ACTION.ZOOM,
      }}
      touches={{
        one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
        two:
          flying && thirdperson && !isOrthoCamera
            ? CameraControlsImpl.ACTION.TOUCH_DOLLY
            : CameraControlsImpl.ACTION.TOUCH_ZOOM,
        three:
          flying && thirdperson && !isOrthoCamera
            ? CameraControlsImpl.ACTION.TOUCH_DOLLY
            : CameraControlsImpl.ACTION.TOUCH_ZOOM,
      }}
      maxZoom={isOrthoCamera ? 100 : 8}
      minZoom={isOrthoCamera ? 4 : 0.4}
      makeDefault
    />
  );
};

Viewer.displayName = 'Viewer';

export default Viewer;
